import { Box, BoxProps, Flex, mdBumps, smBumps, Typo } from '@wrisk/ui-components'
import React, { FunctionComponent } from 'react'

import {
  getEndorsementDifference,
  getMandatoryExcessComparison,
  hasExcessChanged,
  hasUniqueExcesses,
  isExcessEndorsement,
  PolicyVersion,
} from '../../../domain'
import { useExcesses, usePolicyExcessConfig } from '../../../hooks/excess'
import {
  tExcesses,
  TKey,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import { usePolicy } from '../../product/policy/PolicyProvider'
import { Endorsements } from '../endorsements'
import { ExcessRow } from './ExcessRow'

export interface ExcessChangesProps extends BoxProps {
  updatedPolicyVersion: PolicyVersion
}

const tKey = TKey('components.excess-changes')

export const ExcessChanges: FunctionComponent<ExcessChangesProps> = ({
  updatedPolicyVersion: { quote: updatedQuote },
  ...props
}) => {
  const { t } = useWriskTranslation()

  const {
    policy: {
      currentPolicyVersion: { quote: currentQuote },
    },
  } = usePolicy()

  const { main: updatedMain, other: updatedOther } = useExcesses(updatedQuote)
  const { main: currentMain, other: currentOther } = useExcesses(currentQuote)

  const mainComparisons = getMandatoryExcessComparison(currentMain, updatedMain)
  const otherComparisons = getMandatoryExcessComparison(currentOther, updatedOther)

  const hasBreakdown = hasUniqueExcesses(mainComparisons)

  const mainChanges = mainComparisons.filter(hasExcessChanged)
  const otherChanges = otherComparisons.filter(hasExcessChanged)

  const excessConfig = usePolicyExcessConfig()

  const endorsements = getEndorsementDifference(
    updatedQuote.endorsements.filter(isExcessEndorsement(excessConfig)),
    currentQuote.endorsements.filter(isExcessEndorsement(excessConfig)),
  )

  return mainChanges.length || otherChanges.length || endorsements.length ? (
    <Box variant='raised' {...props}>
      <Box p={mdBumps}>
        <Typo fontWeight='bold'>{t(tKey('header'))}</Typo>
      </Box>
      {Boolean(mainChanges.length) && (
        <Flex
          p={mdBumps}
          flexDirection='column'
          alignItems='flex-start'
          rowGap={smBumps}
          borderTopWidth={1}
        >
          {hasBreakdown ? (
            <>
              <Typo typoSize='sm' color='bodySecondary'>
                {t(tKey('sections', 'compulsory'))}
              </Typo>
              {mainChanges.map((it) => (
                <ExcessRow
                  key={it.category}
                  header={t(tExcesses(it.category, 'name'))}
                  currentExcess={it.current}
                  previousExcess={it.previous}
                />
              ))}
            </>
          ) : (
            <ExcessRow
              header={t(tKey('sections', 'compulsory'))}
              currentExcess={mainChanges[0]?.current}
              previousExcess={mainChanges[0]?.previous}
            />
          )}
        </Flex>
      )}
      {Boolean(otherChanges.length) && (
        <Flex
          p={mdBumps}
          flexDirection='column'
          alignItems='flex-start'
          rowGap={smBumps}
          borderTopWidth={1}
        >
          <Typo typoSize='sm' color='bodySecondary'>
            {t(tKey('sections', 'other'))}
          </Typo>
          {otherChanges.map((it) => (
            <ExcessRow
              key={it.category}
              header={t(tExcesses(it.category, 'name'))}
              currentExcess={it.current}
              previousExcess={it.previous}
            />
          ))}
        </Flex>
      )}
      {Boolean(endorsements.length) && (
        <Endorsements
          quote={updatedQuote}
          endorsements={endorsements}
          p={mdBumps}
          borderTopWidth={1}
        />
      )}
    </Box>
  ) : null
}
